import { useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { ShieldCheck, Lock } from "lucide-react";
import { api, inr } from "../lib/api";

const Field = ({ label, testid, error, ...props }) => (
  <div>
    <label className="text-xs uppercase tracking-[0.15em] text-muted-earth">{label}</label>
    <input
      data-testid={testid}
      className={`w-full mt-2 bg-transparent border px-4 py-3.5 text-ink placeholder:text-muted-earth/60 outline-none focus:border-terracotta transition-colors ${error ? "border-destructive" : "border-ink/20"}`}
      {...props}
    />
    {error && <p className="text-destructive text-xs mt-1.5">{error}</p>}
  </div>
);

export const Checkout = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const qty = Math.max(1, parseInt(params.get("qty") || "1", 10));
  const [event, setEvent] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", phone: "", city: "", notes: "" });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    api.event(slug).then((e) => { setEvent(e); setForm((f) => ({ ...f, city: e.city })); }).catch(() => navigate("/events"));
  }, [slug, navigate]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const validate = () => {
    const err = {};
    if (!form.name.trim()) err.name = "Please enter your name";
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email)) err.email = "Enter a valid email";
    if (!/^\+?\d{10,13}$/.test(form.phone.replace(/\s/g, ""))) err.phone = "Enter a valid mobile number";
    if (!form.city.trim()) err.city = "Please enter your city";
    setErrors(err);
    return Object.keys(err).length === 0;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!validate()) { toast.error("Please fix the highlighted fields"); return; }
    setSubmitting(true);
    try {
      const booking = await api.book({ event_id: event.id, quantity: qty, ...form });
      toast.success("Seat reserved!");
      navigate(`/confirmation/${booking.id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not reserve. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (!event) return <div className="max-w-7xl mx-auto px-6 py-40 text-muted-earth">Loading...</div>;

  return (
    <div data-testid="checkout-page" className="max-w-7xl mx-auto px-6 md:px-12 py-14 md:py-20">
      <Link to={`/events/${slug}`} className="text-xs uppercase tracking-[0.15em] text-muted-earth hover:text-terracotta transition-colors">&larr; Back to event</Link>
      <h1 className="font-serif font-light text-4xl md:text-6xl text-ink tracking-tighter mt-6 mb-12">Almost there.</h1>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
        {/* Form */}
        <form onSubmit={submit} data-testid="checkout-form" className="lg:col-span-7 space-y-6">
          <div className="border-l-2 border-terracotta pl-5">
            <p className="text-xs uppercase tracking-[0.15em] text-terracotta">Your details</p>
            <p className="text-muted-earth text-sm mt-1">Required so the host can welcome you personally.</p>
          </div>
          <Field label="Full name" testid="checkout-name" placeholder="e.g. Aarav Sharma" value={form.name} onChange={set("name")} error={errors.name} />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <Field label="Email" testid="checkout-email" placeholder="you@email.com" value={form.email} onChange={set("email")} error={errors.email} />
            <Field label="Mobile number" testid="checkout-phone" placeholder="10-digit number" value={form.phone} onChange={set("phone")} error={errors.phone} />
          </div>
          <Field label="City" testid="checkout-city" placeholder="Your city" value={form.city} onChange={set("city")} error={errors.city} />
          <div>
            <label className="text-xs uppercase tracking-[0.15em] text-muted-earth">Anything we should know? (optional)</label>
            <textarea data-testid="checkout-notes" value={form.notes} onChange={set("notes")} rows={3} placeholder="Dietary needs, coming solo, accessibility..." className="w-full mt-2 bg-transparent border border-ink/20 px-4 py-3.5 text-ink placeholder:text-muted-earth/60 outline-none focus:border-terracotta transition-colors" />
          </div>

          <button data-testid="proceed-payment-button" disabled={submitting} className="w-full bg-terracotta text-cream py-4 uppercase tracking-[0.15em] text-xs flex items-center justify-center gap-2 hover:bg-terracotta-dark transition-colors disabled:opacity-60">
            <Lock size={14} /> {submitting ? "Reserving..." : `Reserve & Continue \u2022 ${inr(event.price * qty)}`}
          </button>
          <p className="flex items-center gap-2 text-xs text-muted-earth"><ShieldCheck size={14} className="text-green-tribe" /> Secure payment via Razorpay will be enabled shortly. Your seat is reserved on submit.</p>
        </form>

        {/* Summary */}
        <div className="lg:col-span-5">
          <div className="border border-ink/15 bg-[hsl(40_25%_92%)] p-7 lg:sticky lg:top-28">
            <p className="text-xs uppercase tracking-[0.15em] text-muted-earth mb-5">Order summary</p>
            <div className="flex gap-4 pb-5 border-b border-ink/10">
              <img src={event.image} alt={event.title} className="w-24 h-24 object-cover border border-ink/15" />
              <div>
                <h3 className="font-serif text-2xl text-ink leading-tight">{event.title}</h3>
                <p className="text-xs uppercase tracking-[0.12em] text-muted-earth mt-2">{event.city}</p>
              </div>
            </div>
            <div className="space-y-3 py-5 text-sm text-muted-earth">
              <div className="flex justify-between"><span>{inr(event.price)} &times; {qty} ticket{qty > 1 ? "s" : ""}</span><span>{inr(event.price * qty)}</span></div>
            </div>
            <div className="flex justify-between items-center pt-5 border-t border-ink/10">
              <span className="text-sm uppercase tracking-[0.12em] text-ink">Total</span>
              <span className="font-serif text-3xl text-ink" data-testid="checkout-total">{inr(event.price * qty)}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
